import { useEffect, useRef, useState } from 'react';
import { Refresh as RefreshIcon } from '@mui/icons-material';
import { jsPDF as JsPdf } from 'jspdf';
import { useReactToPrint } from 'react-to-print';
import {
  DOWNLOAD_CODES,
  ERROR_DOWNLOAD_CODES,
  GENERATE_NEW_CODES,
  PRINT_CODES,
  SUCCESS_DOWNLOAD_CODES,
} from 'common/constants/auth';
import { Button, Grid } from 'components/ui';
import { otpActionButtonSx } from 'pages/Auth/components/OTP/OTP.styles';
import { useResponse } from 'services/hooks/useResponse';
import type { BackupCodesProps } from './BackupCodes.props';
import {
  ActionButtonSlot,
  ButtonsGrid,
  Container,
  DownloadButtonSlot,
  GenerateActionGroup,
  InlineActionsGroup,
  PrimaryActionGroup,
  SecondaryActionsGroup,
} from './BackupCodes.styles';
import { BACKUP_CODES_TEST_IDS } from './BackupCodes.testIds';
import { getBackupCodesFileName } from './BackupCodes.utils';
import { BackupCodesContainer, PrintableBackupCodes } from './components';

const BackupCodes = ({
  backupCodes = [],
  disabledGenerate = false,
  onGenerateNewCodes = () => {},
  onPrimaryAction,
  isAuthFlowStep = false,
  isGeneratingBackupCodes = false,
  primaryActionLabel,
  username = '',
}: BackupCodesProps) => {
  const [doneIsEnabled, setDoneIsEnabled] = useState(false);
  const componentRef = useRef<HTMLDivElement>(null);
  const { processErrorResponse, successNotification } = useResponse();

  useEffect(() => {
    // Any newly issued backup codes should require a fresh download/print acknowledgment
    // before the user can continue.
    setDoneIsEnabled(false);
  }, [backupCodes, isAuthFlowStep]);

  const handlePrint = useReactToPrint({
    contentRef: componentRef,
    onAfterPrint: () => setDoneIsEnabled(true),
  });

  const handleDownload = useReactToPrint({
    onPrintError: error =>
      processErrorResponse({
        error,
        defaultErrorMessage: ERROR_DOWNLOAD_CODES,
      }),
    contentRef: componentRef,
    preserveAfterPrint: true,
    print: async printIframe => {
      const document = printIframe.contentDocument;
      if (document) {
        const html = document.getElementsByTagName('html')[0];
        const pdfExporter = new JsPdf();
        try {
          await pdfExporter.html(html, {
            callback(doc) {
              doc.save(getBackupCodesFileName());
            },
            margin: [10, 10, 10, 10],
            autoPaging: 'text',
            x: 0,
            y: 0,
            width: 190,
            windowWidth: 675,
          });
          successNotification(SUCCESS_DOWNLOAD_CODES);
          setDoneIsEnabled(true);
        } catch (error) {
          processErrorResponse({
            error,
            defaultErrorMessage: ERROR_DOWNLOAD_CODES,
          });
        }
      }
    },
  });

  const generateButton = (
    <Button
      data-testid={BACKUP_CODES_TEST_IDS.generateButton}
      disabled={disabledGenerate || isGeneratingBackupCodes}
      loading={isGeneratingBackupCodes}
      onClick={onGenerateNewCodes}
      sizeVariant="compact"
      startIcon={<RefreshIcon />}
      variant="outlined"
      visualVariant="primaryOutline"
    >
      {GENERATE_NEW_CODES}
    </Button>
  );

  const downloadButton = (
    <Button
      data-testid={BACKUP_CODES_TEST_IDS.downloadButton}
      disabled={backupCodes.length === 0 || !username || isGeneratingBackupCodes}
      onClick={() => handleDownload()}
      sizeVariant="compact"
      variant="text"
      visualVariant="primaryGhost"
    >
      {DOWNLOAD_CODES}
    </Button>
  );

  const printButton = (
    <Button
      data-testid={BACKUP_CODES_TEST_IDS.printButton}
      disabled={backupCodes.length === 0 || !username || isGeneratingBackupCodes}
      onClick={() => handlePrint()}
      sizeVariant="compact"
      variant="text"
      visualVariant="primaryGhost"
    >
      {PRINT_CODES}
    </Button>
  );

  return (
    <BackupCodesContainer>
      <Container isAuthFlowStep={isAuthFlowStep}>
        <PrintableBackupCodes
          backupCodes={backupCodes}
          componentRef={componentRef}
          disabledGenerate={disabledGenerate}
          forPrint
          isAuthFlowStep={isAuthFlowStep}
          isGeneratingBackupCodes={isGeneratingBackupCodes}
          username={username}
        />

        <PrintableBackupCodes
          backupCodes={backupCodes}
          disabledGenerate={disabledGenerate}
          isAuthFlowStep={isAuthFlowStep}
          isGeneratingBackupCodes={isGeneratingBackupCodes}
        />

        <Grid container direction="column">
          <ButtonsGrid
            isAuthFlowStep={isAuthFlowStep}
            container
            justifyContent="flex-end"
            alignItems="center"
          >
            {isAuthFlowStep ? (
              <GenerateActionGroup>
                <ActionButtonSlot>{generateButton}</ActionButtonSlot>
              </GenerateActionGroup>
            ) : null}

            <InlineActionsGroup isAuthFlowStep={isAuthFlowStep}>
              <SecondaryActionsGroup isAuthFlowStep={isAuthFlowStep}>
                {!isAuthFlowStep ? <ActionButtonSlot>{generateButton}</ActionButtonSlot> : null}
                <DownloadButtonSlot>
                  {downloadButton}
                </DownloadButtonSlot>

                <ActionButtonSlot>
                  {printButton}
                </ActionButtonSlot>
              </SecondaryActionsGroup>

              {onPrimaryAction && primaryActionLabel ? (
                <PrimaryActionGroup isAuthFlowStep={isAuthFlowStep}>
                  <Button
                    data-testid={BACKUP_CODES_TEST_IDS.primaryActionButton}
                    disabled={!doneIsEnabled || isGeneratingBackupCodes}
                    onClick={onPrimaryAction}
                    sizeVariant="compact"
                    sx={otpActionButtonSx}
                    variant="contained"
                    visualVariant="positive"
                  >
                    {primaryActionLabel}
                  </Button>
                </PrimaryActionGroup>
              ) : null}
            </InlineActionsGroup>
          </ButtonsGrid>
        </Grid>
      </Container>
    </BackupCodesContainer>
  );
};

export default BackupCodes;
